import styled, { css, keyframes } from "styled-components";

import { StyledViewContainer } from "../../Styled";

export const StyledConfigContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    position: absolute;
    z-index: 1;
    top: 16px;
    right: 16px;

    .ant-btn {
        background-color: ${({ theme }) => theme.primaryBackground} !important;
        border-radius: 50%;

        svg {
            font-size: 18px;
        }
    }

    .ant-select {
        width: 150px !important;
    }
    .ant-select-selector {
        background-color: ${({ theme }) => theme.primaryBackground} !important;
    }
`;

export const StyledTopActions = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    position: absolute;
    z-index: 2;
    top: 16px;
    left: 16px;
`;

export const StyledPreviewContainer = styled(StyledViewContainer)<{
    $backlight: boolean;
    $levelOk: boolean;
}>`
    ${({ theme, $backlight, $levelOk }) => {
        if (!$backlight) return;

        const color = $levelOk
            ? theme.successTypographyColor
            : theme.dangerTypographyColor;

        return css`
            &::after {
                content: "";
                position: absolute;
                inset: 0;
                pointer-events: none;
                z-index: 1;

                box-shadow:
                    inset 0 0 0 1px ${color}4B,
                    inset 0 0 16px 4px ${color}4B;
            }

            ${!$levelOk &&
            css`
                &::before {
                    content: "Тримайте телефон рівно";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                    z-index: 2;

                    padding: 6px 10px;
                    border-radius: 10px;

                    color: ${theme.primaryTypographyColor};
                    background: ${theme.dangerTypographyColor}CC;
                    font-size: 14px;
                    line-height: 1;
                    white-space: nowrap;
                }
            `}
        `;
    }}
`;

export const StyledZoomOverlay = styled.div`
    position: absolute;
    bottom: 90px;
    left: 0;
    right: 0;
    z-index: 5;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    pointer-events: none;

    & > * {
        pointer-events: auto;
    }
`;

const scanMove = keyframes`
    0%   { top: 24px; opacity: 0; }
    8%   { opacity: 1; }
    92%  { opacity: 1; }
    100% { top: calc(100% - 24px); opacity: 0; }
`;

const hintPulse = keyframes`
    0%, 100% { opacity: 0.75; }
    50%       { opacity: 1; }
`;

const cornerFade = keyframes`
    0%, 100% { opacity: 0.6; }
    50%       { opacity: 1; }
`;

export const StyledCapturingOverlay = styled.div<{ $visible: boolean }>`
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.18s ease;
`;

export const StyledCapturingCorners = styled.div`
    position: absolute;
    inset: 0;
    animation: ${cornerFade} 1.4s ease-in-out infinite;

    &::before {
        content: "";
        position: absolute;
        top: 20px;
        left: 20px;
        width: 26px;
        height: 26px;
        border-top: 2.5px solid rgba(255, 255, 255, 0.95);
        border-left: 2.5px solid rgba(255, 255, 255, 0.95);
        border-radius: 3px 0 0 0;
    }

    &::after {
        content: "";
        position: absolute;
        bottom: 20px;
        right: 20px;
        width: 26px;
        height: 26px;
        border-bottom: 2.5px solid rgba(255, 255, 255, 0.95);
        border-right: 2.5px solid rgba(255, 255, 255, 0.95);
        border-radius: 0 0 3px 0;
    }
`;

export const StyledCapturingCornersInner = styled.div`
    position: absolute;
    inset: 0;
    animation: ${cornerFade} 1.4s ease-in-out infinite;

    &::before {
        content: "";
        position: absolute;
        top: 20px;
        right: 20px;
        width: 26px;
        height: 26px;
        border-top: 2.5px solid rgba(255, 255, 255, 0.95);
        border-right: 2.5px solid rgba(255, 255, 255, 0.95);
        border-radius: 0 3px 0 0;
    }

    &::after {
        content: "";
        position: absolute;
        bottom: 20px;
        left: 20px;
        width: 26px;
        height: 26px;
        border-bottom: 2.5px solid rgba(255, 255, 255, 0.95);
        border-left: 2.5px solid rgba(255, 255, 255, 0.95);
        border-radius: 0 0 0 3px;
    }
`;

export const StyledScanLine = styled.div`
    position: absolute;
    left: 20px;
    right: 20px;
    height: 1.5px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.55) 15%,
        rgba(255, 255, 255, 0.95) 50%,
        rgba(255, 255, 255, 0.55) 85%,
        transparent 100%
    );
    animation: ${scanMove} 1.6s ease-in-out infinite;
`;

export const StyledCapturingHint = styled.div`
    position: absolute;
    top: 108px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;

    span {
        padding: 6px 16px;
        border-radius: 20px;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: rgba(255, 255, 255, 0.92);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.03em;
        white-space: nowrap;
        animation: ${hintPulse} 1.6s ease-in-out infinite;
    }
`;

// ---------------------------------------------------------------------------
// Фокус по тапу
// ---------------------------------------------------------------------------

const focusRingAppear = keyframes`
    0%   { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
    18%  { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
    75%  { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
`;

/**
 * Индикатор точки фокуса — жёлтый квадрат с уголками, появляется в месте тапа.
 * $x и $y — координаты в px относительно контейнера (левый верхний угол = 0,0).
 */
export const StyledFocusIndicator = styled.div<{ $x: number; $y: number }>`
    position: absolute;
    left: ${({ $x }) => $x}px;
    top: ${({ $y }) => $y}px;
    width: 62px;
    height: 62px;
    pointer-events: none;
    z-index: 6;
    animation: ${focusRingAppear} 0.9s ease-out forwards;

    /* Уголки вместо полного прямоугольника — классический вид нативной камеры */
    &::before,
    &::after {
        content: "";
        position: absolute;
        width: 14px;
        height: 14px;
        border-color: rgba(255, 214, 0, 0.95);
        border-style: solid;
        transform: translate(-50%, -50%);
    }

    /* Верхний левый + нижний правый */
    &::before {
        top: 0;
        left: 0;
        border-width: 2px 0 0 2px;
        border-radius: 2px 0 0 0;
        transform: none;
    }

    &::after {
        bottom: 0;
        right: 0;
        border-width: 0 2px 2px 0;
        border-radius: 0 0 2px 0;
        transform: none;
    }

    /* Верхний правый + нижний левый через псевдоэлементы дочернего span */
    & > span {
        position: absolute;
        inset: 0;

        &::before,
        &::after {
            content: "";
            position: absolute;
            width: 14px;
            height: 14px;
            border-color: rgba(255, 214, 0, 0.95);
            border-style: solid;
        }

        &::before {
            top: 0;
            right: 0;
            border-width: 2px 2px 0 0;
            border-radius: 0 2px 0 0;
        }

        &::after {
            bottom: 0;
            left: 0;
            border-width: 0 0 2px 2px;
            border-radius: 0 0 0 2px;
        }
    }
`;

export const StyledExposureOverlay = styled.div`
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    pointer-events: none;

    & > * {
        pointer-events: auto;
    }
`;
